import * as Sentry from '@sentry/react';
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { safeLocalStorage, safeSessionStorage } from '../utils/safeStorage';
import type { Plan, PlanFeature, User } from '../types';
import {
    VISAO_CONSOLIDADA,
    corrigirVisaoSemFeature,
    resolverFilialInicial,
    rotaBloqueadaNaVisao,
    valorSalvoDaVisaoConsolidada,
} from './filialAtiva';

/**
 * Token da sessao salvo no navegador. Com o armazenamento bloqueado (aba
 * anonima, iframe, cookies de terceiros bloqueados) ler localStorage lanca
 * SecurityError e o boot ficava preso no carregamento (LTQ-WEB-3W); aqui vira
 * "sem sessao".
 */
export function tokenSalvo(): string | null {
    return safeLocalStorage.getItem('accessToken');
}

/** Tira o guard do toast de vencimento para ele reaparecer no proximo login. */
export function limparAvisoDeVencimento(): void {
    safeSessionStorage.keys()
        .filter(k => k.startsWith('expiry-toast-shown:'))
        .forEach(k => safeSessionStorage.removeItem(k));
}

type Filial = { id: string; name: string; empresaId: string; isActive?: boolean; role?: 'ADMIN' | 'EDITOR' | 'VIEWER'; empresa: { id: string; name: string } };

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    isAdmin: boolean;
    isGerente: boolean;
    isDemo: boolean;
    currentPlan: Plan | null;
    login: (email: string, password: string) => Promise<void>;
    register: (data: { name: string; email: string; password: string; whatsapp: string }) => Promise<void>;
    logout: () => void;
    hasFeature: (feature: PlanFeature) => boolean;
    canAccess: (route: string) => boolean;
    isDemoRoute: (route: string) => boolean;
    filialAtiva: { id: string; name: string } | null;
    filiais: Filial[];
    selecionarFilial: (filialId: string) => Promise<void>;
    isLoadingFiliais: boolean;
    carregarFiliais: () => Promise<void>;
    markOnboardingComplete: () => Promise<void>;
    updateUser: (data: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

const routeToFeature: Record<string, PlanFeature> = {
    '/home': 'dashboard',
    '/lancar-receita': 'transactions',
    '/lancar-despesa': 'transactions',
    '/lancamentos': 'transactions',
    '/cashflow': 'cashflow',
    '/graficos': 'graficos',
    '/reports': 'reports',
    '/plano-contas': 'accounts',
    '/importacoes': 'import',
    '/settings': 'settings',
};

// Rotas integradas com o backend (para usuário demo)
const DEMO_ALLOWED_ROUTES = [
    '/home',
    '/graficos',
    '/plano-contas',
    '/lancamentos',
    '/lancar-receita',
    '/lancar-despesa',
    '/cashflow',
    '/importar-lancamentos',
    '/reports',
    '/help',
    '/settings',
];

const DEMO_EMAIL = 'demo@lqt.com';

export function AuthProvider({ children }: AuthProviderProps) {
    const [user, setUser] = useState<User | null>(null);
    const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [filialAtiva, setFilialAtiva] = useState<{ id: string; name: string } | null>(null);
    const [filiais, setFiliais] = useState<Filial[]>([]);
    const [isLoadingFiliais, setIsLoadingFiliais] = useState(false);
    const navigate = useNavigate();

    const isAdmin = user?.role === 'ADMIN';
    const isGerente = user?.role === 'GERENTE' || user?.role === 'ADMIN';
    const isDemo = user?.email === DEMO_EMAIL;

    // Identifica o usuario no Sentry. Um unico efeito cobre todos os caminhos
    // que mexem em `user` (login, cadastro, restauracao de sessao no boot e
    // logout), em vez de espalhar chamadas por cada um deles.
    useEffect(() => {
        if (user) {
            Sentry.setUser({ id: user.id, email: user.email, username: user.name });
        } else {
            Sentry.setUser(null);
        }
    }, [user]);

    // Mid-session suspension: backend returns 402 → api.ts fires this event
    useEffect(() => {
        const handler = () => {
            // Guard: only update if not already suspended to avoid infinite re-renders
            setUser(prev => (prev && prev.status !== 'suspended') ? { ...prev, status: 'suspended' } : prev);
        };
        window.addEventListener('account-suspended', handler);
        return () => window.removeEventListener('account-suspended', handler);
    }, []);

    const carregarFiliais = useCallback(async () => {
        setIsLoadingFiliais(true);
        try {
            const data = await api.getMinhasFiliais();
            setFiliais(data);

            // Conta sem nenhuma filial (o caso do admin criado pelo admin:create,
            // unico fluxo que nao provisiona empresa/filial). Nao existe contexto
            // de filial para assumir: manter filialAtiva nula impede o
            // FinanceContext de disparar as chamadas de dashboard, que o
            // FilialGuard rejeita com 403 "Sem acesso a nenhuma filial".
            if (data.length === 0) {
                setFilialAtiva(null);
                safeLocalStorage.removeItem('filialAtiva');
                return;
            }

            // Regras em filialAtiva.ts: padrao e a Matriz, e "Todos os negocios"
            // salvo so volta se foi escolhido no seletor.
            const { ativa, regravar } = resolverFilialInicial(data, safeLocalStorage.getItem('filialAtiva'));
            setFilialAtiva(ativa);
            if (regravar && ativa) {
                safeLocalStorage.setItem('filialAtiva', JSON.stringify(ativa));
            }
        } catch {
            // silencia, filiais ficam vazias
        } finally {
            setIsLoadingFiliais(false);
        }
    }, []);

    useEffect(() => {
        const checkAuth = async () => {
            const token = tokenSalvo();
            if (token) {
                try {
                    const profile = await api.getProfile();
                    setUser({
                        id: profile.id,
                        name: profile.name,
                        email: profile.email,
                        role: profile.role,
                        status: profile.status?.toLowerCase() as 'active' | 'inactive' | 'suspended',
                        planId: profile.planId,
                        onboardingCompletedAt: profile.onboardingCompletedAt ?? null,
                        surveyOnboardingRequired: profile.surveyOnboardingRequired ?? false,
                        surveyOnboardingCompletedAt: profile.surveyOnboardingCompletedAt ?? null,
                    });
                    if (profile.plan) {
                        setCurrentPlan({
                            id: profile.plan.id,
                            name: profile.plan.name,
                            description: profile.plan.description || '',
                            price: profile.plan.price,
                            interval: profile.plan.interval ?? 'monthly',
                            features: profile.plan.features || [],
                            isActive: profile.plan.isActive,
                            maxAccounts: profile.plan.maxAccounts ?? 0,
                            maxTransactionsPerMonth: profile.plan.maxTransactionsPerMonth ?? 0,
                            createdAt: profile.plan.createdAt ?? '',
                            updatedAt: profile.plan.updatedAt ?? '',
                        });
                    }
                    await carregarFiliais();
                } catch {
                    safeLocalStorage.removeItem('accessToken');
                    safeLocalStorage.removeItem('refreshToken');
                }
            }
            setIsInitialized(true);
        };
        checkAuth();
    }, [carregarFiliais]);

    const hasFeature = useCallback((feature: PlanFeature): boolean => {
        if (!user) return false;
        if (isAdmin) return true;
        if (!currentPlan) return false;
        return currentPlan.features.includes(feature);
    }, [user, isAdmin, currentPlan]);

    // Sem a feature de filiais o seletor nao aparece, entao quem estiver na visao
    // consolidada (escolha de quando tinha a feature, por exemplo) ficaria preso
    // sem poder lancar. Plano e usuario chegam juntos do /auth/profile, antes das
    // filiais, entao aqui o hasFeature ja reflete o plano real.
    useEffect(() => {
        if (!user) return;
        const destino = corrigirVisaoSemFeature(filialAtiva, filiais, hasFeature('filiais'));
        if (!destino) return;
        setFilialAtiva(destino);
        safeLocalStorage.setItem('filialAtiva', JSON.stringify(destino));
    }, [user, filialAtiva, filiais, hasFeature]);

    const canAccess = useCallback((route: string): boolean => {
        if (!user) return false;
        // "Todos os negocios" e visao consolidada: mostra numeros de todas as
        // filiais, mas nao tem uma filial de destino para receber lancamento
        // (antes ia calado para a primeira, quase sempre a Matriz).
        if (rotaBloqueadaNaVisao(route, filialAtiva?.id)) return false;
        if (isAdmin) return true;
        if (route.startsWith('/admin')) return false;
        if (isDemo) return DEMO_ALLOWED_ROUTES.includes(route);
        const feature = routeToFeature[route];
        if (!feature) return true;
        return hasFeature(feature);
    }, [user, isAdmin, isDemo, hasFeature, filialAtiva]);

    const isDemoRoute = useCallback((route: string): boolean => {
        return DEMO_ALLOWED_ROUTES.includes(route);
    }, []);

    const login = useCallback(async (email: string, password: string) => {
        setIsLoading(true);
        try {
            const response = await api.login(email, password);

            safeLocalStorage.setItem('accessToken', response.accessToken);
            safeLocalStorage.setItem('refreshToken', response.refreshToken);
            // Cada login deve poder reexibir o toast de vencimento
            limparAvisoDeVencimento();

            const userData = response.user;
            setUser({
                id: userData.id,
                name: userData.name,
                email: userData.email,
                role: userData.role as 'ADMIN' | 'GERENTE' | 'OPERADOR',
                status: userData.status?.toLowerCase() as 'active' | 'inactive' | 'suspended',
                planId: userData.planId,
                onboardingCompletedAt: userData.onboardingCompletedAt ?? null,
                surveyOnboardingRequired: userData.surveyOnboardingRequired ?? false,
                surveyOnboardingCompletedAt: userData.surveyOnboardingCompletedAt ?? null,
            });
            if (userData.plan) {
                setCurrentPlan({
                    id: userData.plan.id,
                    name: userData.plan.name,
                    description: userData.plan.description || '',
                    price: userData.plan.price,
                    interval: userData.plan.interval ?? 'monthly',
                    features: userData.plan.features || [],
                    isActive: userData.plan.isActive,
                    maxAccounts: userData.plan.maxAccounts ?? 0,
                    maxTransactionsPerMonth: userData.plan.maxTransactionsPerMonth ?? 0,
                    createdAt: userData.plan.createdAt ?? '',
                    updatedAt: userData.plan.updatedAt ?? '',
                });
            }

            await carregarFiliais();

            const role = userData.role?.toLowerCase();
            if (role === 'admin') {
                navigate('/admin');
            } else {
                navigate('/graficos');
            }
        } finally {
            setIsLoading(false);
        }
    }, [carregarFiliais, navigate]);

    const register = useCallback(async (data: { name: string; email: string; password: string; whatsapp: string }) => {
        setIsLoading(true);
        try {
            const response = await api.register(data);

            safeLocalStorage.setItem('accessToken', response.accessToken);
            safeLocalStorage.setItem('refreshToken', response.refreshToken);

            const userData = response.user;
            setUser({
                id: userData.id,
                name: userData.name,
                email: userData.email,
                role: userData.role as 'ADMIN' | 'GERENTE' | 'OPERADOR',
                status: userData.status?.toLowerCase() as 'active' | 'inactive' | 'suspended',
                planId: userData.planId,
                onboardingCompletedAt: userData.onboardingCompletedAt ?? null,
                surveyOnboardingRequired: userData.surveyOnboardingRequired ?? false,
                surveyOnboardingCompletedAt: userData.surveyOnboardingCompletedAt ?? null,
            });
            if (userData.plan) {
                setCurrentPlan({
                    id: userData.plan.id,
                    name: userData.plan.name,
                    description: userData.plan.description || '',
                    price: userData.plan.price,
                    interval: userData.plan.interval ?? 'monthly',
                    features: userData.plan.features || [],
                    isActive: userData.plan.isActive,
                    maxAccounts: userData.plan.maxAccounts ?? 0,
                    maxTransactionsPerMonth: userData.plan.maxTransactionsPerMonth ?? 0,
                    createdAt: userData.plan.createdAt ?? '',
                    updatedAt: userData.plan.updatedAt ?? '',
                });
            }

            await carregarFiliais();
            navigate('/home');
        } finally {
            setIsLoading(false);
        }
    }, [carregarFiliais, navigate]);

    const selecionarFilial = useCallback(async (filialId: string) => {
        if (filialId === 'all') {
            setFilialAtiva(VISAO_CONSOLIDADA);
            safeLocalStorage.setItem('filialAtiva', valorSalvoDaVisaoConsolidada());
            return;
        }

        const filial = filiais.find(f => f.id === filialId);
        if (filial) {
            setFilialAtiva({ id: filial.id, name: filial.name });
            safeLocalStorage.setItem('filialAtiva', JSON.stringify({ id: filial.id, name: filial.name }));
        }
    }, [filiais]);

    const logout = useCallback(() => {
        safeLocalStorage.removeItem('accessToken');
        safeLocalStorage.removeItem('refreshToken');
        safeLocalStorage.removeItem('filialAtiva');
        // Limpa o guard do toast de vencimento para reaparecer no próximo login
        limparAvisoDeVencimento();
        setUser(null);
        setCurrentPlan(null);
        setFilialAtiva(null);
        setFiliais([]);
    }, []);

    const markOnboardingComplete = useCallback(async () => {
        await api.markOnboardingComplete();
        setUser(prev => prev ? { ...prev, onboardingCompletedAt: new Date().toISOString() } : prev);
    }, []);

    const updateUser = useCallback((data: Partial<User>) => {
        setUser(prev => prev ? { ...prev, ...data } : prev);
    }, []);

    if (!isInitialized) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: '#0e1a15',
                color: '#fff'
            }}>
                Carregando...
            </div>
        );
    }

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated: !!user,
                isLoading,
                isAdmin,
                isGerente,
                isDemo,
                currentPlan,
                login,
                register,
                logout,
                hasFeature,
                canAccess,
                isDemoRoute,
                filialAtiva,
                filiais,
                selecionarFilial,
                isLoadingFiliais,
                carregarFiliais,
                markOnboardingComplete,
                updateUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}